import { ValueModel } from '@kts-front/models';
import dayjs from 'dayjs';
import { action, makeObservable } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { MeetingSlotEntityApi } from 'entities/Meeting';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { OptionEntity } from 'types/OptionEntity';
import { isDefined } from 'utils/isDefined';

import { FieldSelectMultiStore, FieldSelectStoreOptions } from '../form/FieldSelectStore';

type FieldSelectSlotOptions = Omit<FieldSelectStoreOptions<OptionEntity>, 'getOptions'> & {
  getLeadId?: () => number | null;
};

export class FieldSelectTimeSlotStore extends FieldSelectMultiStore {
  readonly date = new ValueModel<string>('');
  readonly projectId = new ValueModel<string>('');
  readonly isOffline = new ValueModel<boolean>(false);

  private readonly _getLeadId?: () => number | null;

  constructor({ getLeadId, ...options }: FieldSelectSlotOptions) {
    super({
      ...options,
      getOptions: async (params) => {
        const leadId = this._getLeadId?.();
        const leadParam = isDefined(leadId) ? `&lead_id=${leadId}` : '';
        const response = await apiStore
          .createRequest({
            url: `${ENDPOINTS.meeting.slots}?project_id=${this.projectId.value}&date=${this.date.value}&is_offline=${this.isOffline.value}${leadParam}`,
          })
          .call<MeetingSlotEntityApi[]>({
            params: {
              limit: params.limit,
              offset: params.offset,
              search: params.search,
            },
          });

        const requestPayload = {
          limit: params.limit,
          offset: params.offset,
          search: params.search,
        };

        if (response.isError) {
          logApiErrWithSentry({
            endpoint: ENDPOINTS.meeting.slots,
            method: 'GET',
            requestPayload,
            response,
          });

          return {
            isError: true,
            data: { items: [], total: 0 },
          };
        }

        return {
          isError: false,
          data: {
            items: response.data.map((item) => ({
              key: item.id.toString(),
              value: `${dayjs(item.start_date).format('HH:mm')} - ${dayjs(item.end_date).format('HH:mm')}`,
            })),
            total: 0,
          },
        };
      },
    });

    this._getLeadId = getLeadId;

    makeObservable(this, {
      updateSlot: action.bound,
    });
  }

  updateSlot = (date: string, projectId: string, isOffline: boolean) => {
    if (this.date.value === date && this.projectId.value === projectId && this.isOffline.value === isOffline) {
      return;
    }

    // Сбрасываем опции перед обновлением параметров
    this.resetOptions();

    this.date.change(date);
    this.projectId.change(projectId);
    this.isOffline.change(isOffline);

    this.load(true);
  };

  reset() {
    this.date.change('');
    this.projectId.change('');
    this.isOffline.change(false);
    this.resetOptions();
  }
}
